import React from "react";
import { useField } from "formik";
import { useTheme } from "../../providers/ThemeContext";
import { lightTheme } from "../../theme/theme";

type Theme = typeof lightTheme;

type Props = {
  name: string;
  label: string;
  type?: "text" | "email" | "tel" | "password" | "number" | "textarea";
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
  rows?: number;
  hint?: string;
  disabled?: boolean;
  maxLength?: number;
};

export default function InputField(properties: Props) {
  const {
    name,
    label,
    type = "text",
    placeholder,
    autoComplete,
    required,
    rows = 4,
    hint,
    disabled,
    maxLength,
  } = properties;

  const { theme, isDark } = useTheme();
  const [field, meta] = useField<string>(name);
  const invalid = Boolean(meta.touched && meta.error);
  const s = InputStyles(theme, isDark, invalid, disabled === true);

  return (
    <div style={s.wrap}>
      <label htmlFor={name} style={s.label}>
        {label} {required ? <span aria-hidden="true">*</span> : null}
      </label>

      {type === "textarea" ? (
        <textarea
          id={name}
          {...field}
          rows={rows}
          placeholder={placeholder}
          autoComplete={autoComplete}
          disabled={disabled}
          maxLength={maxLength}
          style={s.inputTextarea}
        />
      ) : (
        <input
          id={name}
          {...field}
          type={type}
          placeholder={placeholder}
          autoComplete={autoComplete}
          disabled={disabled}
          maxLength={maxLength}
          style={s.input}
        />
      )}

      {invalid ? (
        <div style={s.error} role="alert">
          {meta.error}
        </div>
      ) : hint ? (
        <div style={s.hint}>{hint}</div>
      ) : null}
    </div>
  );
}

const InputStyles = (
  theme: Theme,
  isDark: boolean,
  invalid: boolean,
  disabled: boolean
) => {
  const borderColor = invalid
    ? theme.error
    : isDark
    ? theme.background
    : theme.border;

  const textColor = theme.text;
  const muted = theme.muted;
  const border = "2px";

  const baseInput: React.CSSProperties = {
    fontFamily: "'Poppins', sans-serif",
    fontSize: 14,
    color: textColor,
    lineHeight: 1.4,
    // Follow the document direction so tel/email inputs (which browsers force
    // to LTR by default) align like every other field in an RTL form.
    direction: "inherit",
    textAlign: "start",
    background: theme.background ?? "#fff",
    border: `${border} solid ${borderColor}`,
    borderRadius: 10,
    padding: "12px 14px",
    outline: "none",
    transition: "border-color .18s ease, box-shadow .18s ease",
    ...(disabled ? { opacity: 0.6, cursor: "not-allowed" } : {}),
  };

  return {
    wrap: {
      display: "flex",
      flexDirection: "column" as const,
      gap: 6,
      marginBottom: 12,
    },
    label: {
      fontSize: 12,
      letterSpacing: ".08em",
      textTransform: "uppercase" as const,
      color: muted,
      fontWeight: 700,
      fontFamily: "'Poppins', sans-serif",
    },
    input: baseInput,
    inputTextarea: {
      ...baseInput,
      resize: "vertical" as const,
      minHeight: 120,
    },
    hint: {
      color: muted,
      fontSize: 12,
    },
    error: {
      color: theme.error,
      fontSize: 12,
    },
  };
};
